import { useCallback } from 'react'
import { useMutation } from 'react-query'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { useMarkPurchasesStepDone } from '@features/quickStart/hooks'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useAttachmentsFilters } from '../../../hooks/useAttachmentsFilters'
import { useScoutConnectionId } from '../../../hooks/useScoutConnectionId'
import {
  createScoutConnectionImap,
  CreateScoutConnectionImapPayload,
  CreateScoutConnectionImapResponse,
} from '../../../query-api'
import { ScoutConnectionImapForm } from '../types/scoutConnectionImapForm'

interface UseCreateScoutConnectionImapProps {
  onSuccess?: () => void
  onError?: () => void
}

export const useCreateScoutConnectionImap = (props?: UseCreateScoutConnectionImapProps) => {
  const { organization } = useUserOrganization()
  const { bookkeepingBankAccounts } = useBookkeepingBankAccounts()
  const { setScoutConnectionId } = useScoutConnectionId()
  const [, setFilters] = useAttachmentsFilters()
  const markPurchasesStepDone = useMarkPurchasesStepDone()

  const { onSuccess, onError } = props || {}

  const { mutate: createScoutConnectionImapMutate, isLoading: isProcessing } = useMutation<
    CreateScoutConnectionImapResponse,
    unknown,
    CreateScoutConnectionImapPayload
  >(
    async (payload: CreateScoutConnectionImapPayload) => {
      return createScoutConnectionImap(payload)
    },
    {
      onSuccess: (response?: CreateScoutConnectionImapResponse) => {
        const scoutConnectionId = response?.connectionId
        scoutConnectionId && setScoutConnectionId(scoutConnectionId)
        setFilters({ type: AttachmentFilterType.Scout })
        markPurchasesStepDone()
        onSuccess?.()
      },
      onError: () => {
        onError?.()
      },
    },
  )

  const create = useCallback(
    (formData: ScoutConnectionImapForm) => {
      const accountIds = bookkeepingBankAccounts?.map((account) => account.id) || []
      return (
        organization &&
        createScoutConnectionImapMutate({
          ...formData,
          organizationId: organization.id,
          organizationUrl: organization.url,
          accountIds,
        })
      )
    },
    [createScoutConnectionImapMutate, organization, bookkeepingBankAccounts],
  )

  return { create, isProcessing }
}
